import { httpsCallable } from "firebase/functions";

export const RUNTIME_LIMITS = Object.freeze({ episodes: 8, transitions: 8, submissions: 4, bytes: 8192, episodeMs: 30000 });
const ERROR_DETAILS = new Set([
  "fragLoadError", "fragLoadTimeOut", "levelLoadError", "levelLoadTimeOut",
  "manifestLoadError", "manifestLoadTimeOut", "bufferStalledError", "bufferFullError",
  "bufferAppendError", "bufferAppendingError", "bufferAppendNoProgress",
  "mediaSourceRequiresReset", "internalException", "keyLoadError", "keyLoadTimeOut",
  "fragDecryptError", "fragParsingError", "remuxAllocError", "bufferAddCodecError",
  "bufferIncompatibleCodecsError", "bufferSeekOverHole", "bufferNudgeOnStall",
  "audioTrackLoadError", "audioTrackLoadTimeOut", "nativeMediaError", "providerError",
]);
const ERROR_TYPES = new Set(["networkError", "mediaError", "muxError", "otherError", "keySystemError"]);
const TERMINALS = new Set(["fatal", "ended", "pagehide", "provider-changed", "source-changed", "player-reinitialized", "unmount"]);
const saturate = (value) => Math.min(10000, value + 1);
const num = (value, max, min = 0) => typeof value === "number" && Number.isFinite(value)
  ? Math.max(min, Math.min(max, value)) : undefined;
const compact = (value) => Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined));
const safely = (fn) => { try { return fn(); } catch { return undefined; } };

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (n) => n.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function environment(navigatorValue, documentValue) {
  const ua = String(navigatorValue?.userAgent || "");
  const family = /Edg\//.test(ua) ? "edge" : /(?:Chrome|CriOS)\//.test(ua) ? "chrome"
    : /(?:Firefox|FxiOS)\//.test(ua) ? "firefox" : /Safari\//.test(ua) ? "safari" : "other";
  const major = ua.match(/(?:Edg|Chrome|CriOS|Firefox|FxiOS|Version)\/(\d+)/)?.[1];
  const scripts = Array.from(documentValue?.scripts || []);
  const bundle = scripts.map((s) => String(s.src || "").match(/\/(main\.[a-f0-9]{8,64}\.js)(?:$|\?)/)?.[1]).find(Boolean);
  return compact({
    browserFamily: family, browserVersionMajor: major ? num(Number(major), 999, 1) : undefined,
    osFamily: /iPhone|iPad|iPod/.test(ua) ? "ios" : /Android/.test(ua) ? "android"
      : /Windows/.test(ua) ? "windows" : /Mac OS X/.test(ua) ? "macos" : /Linux/.test(ua) ? "linux" : "other",
    deviceClass: /Mobi|Android|iPhone|iPad|iPod/.test(ua) ? "mobile" : "desktop",
    applicationVersion: bundle || "development",
  });
}

// All reads are optional. No media mutation, promise awaiting or cross-origin request.
export function runtimeMediaState(media, { documentValue = document, navigatorValue = navigator, credentialExp, nowMs = Date.now() } = {}) {
  const currentTime = num(safely(() => media.currentTime), 86400);
  let bufferedAhead;
  let bufferedStart;
  let bufferedEnd;
  const ranges = safely(() => media.buffered);
  const rawLength = safely(() => ranges.length);
  const length = num(rawLength, 16);
  let containing = false;
  if (length !== undefined && currentTime !== undefined) {
    bufferedAhead = 0;
    for (let i = 0; i < length; i++) {
      const start = safely(() => ranges.start(i));
      const end = safely(() => ranges.end(i));
      if (Number.isFinite(start) && Number.isFinite(end) &&
          (bufferedStart === undefined || Math.min(Math.abs(start - currentTime), Math.abs(end - currentTime)) <
            Math.min(Math.abs(bufferedStart - currentTime), Math.abs(bufferedEnd - currentTime)))) {
        bufferedStart = num(start, 86400); bufferedEnd = num(end, 86400);
      }
      if (typeof start === "number" && start <= currentTime && end >= currentTime) {
        bufferedStart = num(start, 86400); bufferedEnd = num(end, 86400);
        bufferedAhead = num(end - currentTime, 86400);
        containing = true;
        break;
      }
    }
    // An uninspected range could contain the playhead: report unknown, not zero.
    if (rawLength > 16 && !containing) bufferedAhead = bufferedStart = bufferedEnd = undefined;
  }
  return compact({
    currentTime, duration: num(safely(() => media.duration), 86400), bufferedAhead, bufferedStart, bufferedEnd,
    readyState: num(safely(() => media.readyState), 4), networkState: num(safely(() => media.networkState), 3),
    paused: typeof safely(() => media.paused) === "boolean" ? media.paused : undefined,
    seeking: typeof safely(() => media.seeking) === "boolean" ? media.seeking : undefined,
    visibilityState: documentValue?.visibilityState === "hidden" ? "hidden" : "visible",
    online: navigatorValue?.onLine !== false,
    credentialRemainingSeconds: Number.isFinite(credentialExp)
      ? num(Math.floor(credentialExp - nowMs / 1000), 86400, -86400) : undefined,
  });
}

export function runtimeErrorContext(data = {}, requestDetailAvailable = false) {
  const details = ERROR_DETAILS.has(data.details) ? data.details : "other";
  const result = compact({
    errorType: ERROR_TYPES.has(data.type) ? data.type : "otherError",
    errorDetails: details, mediaErrorCode: num(data.mediaErrorCode, 4),
  });
  if (!requestDetailAvailable) return result;
  const url = data.context?.url || data.url || data.frag?.url;
  const host = safely(() => new URL(url).hostname.toLowerCase());
  const hostname = host === "stream.mux.com" ? host : host && /(?:^|\.)mux(?:ed\.dev|\.com)$/.test(host) ? "mux-delivery" : "other";
  const requestClass = /fragLoad/.test(details) ? "media-fragment" : /manifestLoad/.test(details) ? "master-manifest"
    : /levelLoad|audioTrackLoad/.test(details) ? "level-playlist" : /keyLoad/.test(details) ? "encryption-key" : "other";
  const stats = data.stats || data.frag?.stats;
  return { ...result, ...compact({ hostname, requestClass,
    httpStatus: num(data.response?.code, 599), bytesLoaded: num(stats?.loaded, 1e9),
    retryCount: num(stats?.retry, 10000),
    loaderDurationMs: stats?.loading?.end > 0
      ? num(stats.loading.end - stats.loading.start, 86400000) : undefined,
  }) };
}

export function createPlaybackRuntimeSession({ provider, engine, transport, readState = () => ({}),
  now = Date.now, monotonic = () => performance.now(), id = randomId,
  schedule = setTimeout, cancel = clearTimeout, navigatorValue = navigator, documentValue = document,
  startReason = "player-reinitialized" } = {}) {
  if (!["cloudflare", "mux"].includes(provider)) throw new Error("Unsupported runtime provider");
  if (!(provider === "cloudflare" ? engine === "cloudflare-sdk" : ["hls.js", "native-hls"].includes(engine))) {
    throw new Error("Unsupported runtime engine");
  }
  const playbackSessionId = id();
  const base = { schemaVersion: 1, playbackSessionId, provider, engine,
    requestDetailAvailable: engine === "hls.js", startReason: TERMINALS.has(startReason) ? startReason : "player-reinitialized",
    ...environment(navigatorValue, documentValue) };
  let initialPlayingAt;
  let closed = false;
  let paused = false;
  let seeking = false;
  let active = null;
  let pending = [];
  let firstError;
  let pendingError;
  let episodeCount = 0;
  let droppedEpisodeCount = 0;
  let submissionCount = 0;
  let errorCount = 0;
  let timer;
  const transitions = [];
  const timestamp = () => new Date(now()).toISOString();
  const state = () => {
    const raw = safely(readState) || {};
    return compact({ currentTime: num(raw.currentTime, 86400), duration: num(raw.duration, 86400),
      bufferedStart: num(raw.bufferedStart, 86400), bufferedEnd: num(raw.bufferedEnd, 86400),
      bufferedAhead: num(raw.bufferedAhead, 86400), readyState: num(raw.readyState, 4), networkState: num(raw.networkState, 3),
      paused: typeof raw.paused === "boolean" ? raw.paused : undefined,
      seeking: typeof raw.seeking === "boolean" ? raw.seeking : undefined,
      visibilityState: ["visible", "hidden"].includes(raw.visibilityState) ? raw.visibilityState : undefined,
      online: typeof raw.online === "boolean" ? raw.online : undefined,
      credentialRemainingSeconds: num(raw.credentialRemainingSeconds, 86400, -86400),
    });
  };
  const boundary = (snapshot, suffix) => Object.fromEntries(Object.entries(snapshot).map(([k, v]) => [k + suffix, v]));
  const clearTimer = () => { if (timer !== undefined) cancel(timer); timer = undefined; };
  const elapsed = (start) => num(monotonic() - start, 86400000) || 0;

  function finishInterval() {
    if (active?.waitingSince !== undefined) {
      active.summary.durationMs = Math.min(86400000, active.summary.durationMs + elapsed(active.waitingSince));
      active.waitingSince = undefined;
    }
  }
  function snapshotEpisode(terminal = false) {
    if (!active) return null;
    return { ...active.summary, durationMs: Math.min(86400000, active.summary.durationMs +
      (active.waitingSince === undefined ? 0 : elapsed(active.waitingSince))),
      endAt: timestamp(), recovered: active.waitingSince === undefined && active.summary.recovered,
      terminal, ...boundary(state(), "End") };
  }
  function send(terminalReason, fatalContext) {
    const terminal = !!terminalReason;
    if (!initialPlayingAt || submissionCount >= (terminal ? 4 : 3)) return;
    const payload = { ...base, initialPlayingAt, submissionSequence: submissionCount + 1,
      submittedAt: timestamp(), terminal, ...(terminal ? { terminalReason } : {}),
      episodeCount, droppedEpisodeCount, errorCount,
      ...(terminal ? boundary(state(), "Terminal") : {}),
      transitions: transitions.slice(), episodes: pending.slice(),
      ...(firstError ? Object.fromEntries(Object.entries(firstError).map(([k, v]) => ["first" + k[0].toUpperCase() + k.slice(1), v])) : {}),
      ...(fatalContext ? { fatalErrorDetails: fatalContext.errorDetails,
        ...(fatalContext.mediaErrorCode !== undefined ? { fatalMediaErrorCode: fatalContext.mediaErrorCode } : {}) } : {}),
    };
    // Every serialized string is generated ASCII or an allowlisted enum.
    const bytes = () => JSON.stringify(payload).length;
    while (bytes() > RUNTIME_LIMITS.bytes && payload.episodes.length) {
      payload.episodes.shift();
      payload.droppedEpisodeCount = saturate(payload.droppedEpisodeCount);
    }
    if (bytes() > RUNTIME_LIMITS.bytes) return;
    submissionCount++;
    pending = [];
    // Scheduling and rejection handling are independent of media continuation.
    Promise.resolve().then(() => transport(payload)).catch(() => undefined);
  }
  function retain(summary) {
    pending = pending.filter((item) => item.episodeId !== summary.episodeId);
    if (pending.length === 7) { pending.shift(); droppedEpisodeCount = saturate(droppedEpisodeCount); }
    pending.push(summary);
  }
  function deadline() {
    timer = undefined;
    if (closed || !active) return;
    retain(snapshotEpisode());
    send();
    if (active.waitingSince === undefined) active = null;
    else active.reported = true; // One unresolved snapshot; never a polling loop.
  }
  function begin() {
    if (!active) {
      if (episodeCount >= 8) { droppedEpisodeCount = saturate(droppedEpisodeCount); return; }
      episodeCount++;
      active = { summary: { episodeId: id(), startAt: timestamp(), durationMs: 0,
        waitingCount: 0, stalledCount: 0, recovered: false, terminal: false,
        ...boundary(state(), "Start"), ...(pendingError || {}) } };
      pendingError = undefined;
      timer = schedule(() => safely(deadline), RUNTIME_LIMITS.episodeMs);
    }
    if (active.waitingSince === undefined) active.waitingSince = monotonic();
    active.summary.recovered = false;
  }
  function close(reason = "unmount", fatalContext) {
    if (closed) return;
    clearTimer();
    if (active) { retain(snapshotEpisode(true)); active = null; }
    send(TERMINALS.has(reason) ? reason : "unmount", fatalContext);
    closed = true;
  }
  return {
    playbackSessionId,
    event(name) {
      if (closed) return;
      if (["offline", "online", "hidden", "visible"].includes(name)) {
        if (transitions.length < 8) transitions.push({ event: name, at: timestamp() });
        return;
      }
      if (name === "pause" || name === "seeking") {
        if (name === "pause") paused = true; else seeking = true;
        if (active?.waitingSince !== undefined) {
          finishInterval();
          active.summary.interruptedBy = name;
          retain(snapshotEpisode()); active = null; clearTimer();
        }
        return;
      }
      if (name === "play") { paused = false; return; }
      if (name === "seeked") { seeking = false; return; }
      if (name === "playing") {
        paused = false; seeking = false;
        if (!initialPlayingAt) initialPlayingAt = timestamp();
        if (active?.waitingSince !== undefined) {
          finishInterval(); active.summary.recovered = true;
          active.summary.recoveredAt = timestamp();
          if (active.reported) { retain(snapshotEpisode()); active = null; send(); }
        }
        return;
      }
      if (name === "ended" || name === "pagehide") { close(name); return; }
      if (!initialPlayingAt || !["waiting", "stalled"].includes(name)) return;
      // Raw stalled reports media fetching, not necessarily interrupted playback.
      // Retain it only as context during an actual waiting interval, never as its anchor.
      if (name === "stalled" && active?.waitingSince === undefined) return;
      const media = state();
      if (paused || seeking || media.paused === true || media.seeking === true) return;
      if (name === "stalled") {
        active.summary.stalledCount = saturate(active.summary.stalledCount);
        return;
      }
      begin();
      if (active) active.summary.waitingCount = saturate(active.summary.waitingCount);
    },
    error(data) {
      if (closed || !initialPlayingAt) return;
      errorCount = saturate(errorCount);
      const context = runtimeErrorContext(data, base.requestDetailAvailable);
      if (!firstError) firstError = context;
      if (!active && !pendingError) pendingError = context;
      if (active && !active.summary.errorDetails) Object.assign(active.summary, context);
      // Nonfatal bufferStalledError remains context; it must not synthesize waiting.
      if (data.fatal === true) { begin(); close("fatal", context); }
    },
    close,
    // Test-only inspection: no session/episode values are attached to window or the UI.
    inspect: () => ({ playbackSessionId, episodeCount, submissionCount, closed,
      heldEpisodes: pending.length + (active ? 1 : 0), heldTransitions: transitions.length }),
  };
}

// Identity values stay local; only this enum crosses the diagnostic boundary.
export function runtimeEndReason(previous, current) {
  if (previous.provider !== current.provider) return "provider-changed";
  if (previous.lesson !== current.lesson || previous.source !== current.source) return "source-changed";
  if (previous.generation !== current.generation) return "player-reinitialized";
  return "unmount";
}

export function attachPlaybackRuntimeDiagnostics({ media, provider, engine, functions,
  credentialExp, startReason, transport, ...options }) {
  const removers = [];
  let session;
  const invoke = (method, ...args) => safely(() => session?.[method](...args));
  try {
    session = createPlaybackRuntimeSession({ provider, engine, startReason, ...options,
      transport: transport || ((payload) => httpsCallable(functions, "logPlaybackRuntimeDiagnostic")(payload)),
      readState: () => runtimeMediaState(media, { credentialExp }),
    });
    const listen = (target, event, fn) => {
      target.addEventListener(event, fn);
      removers.push(() => target.removeEventListener(event, fn));
    };
    ["play", "playing", "waiting", "stalled", "pause", "seeking", "seeked", "ended", "loadedmetadata"].forEach((name) =>
      listen(media, name, () => invoke("event", name)));
    if (engine !== "hls.js") listen(media, "error", () => invoke("error", {
      details: provider === "cloudflare" ? "providerError" : "nativeMediaError", type: "mediaError",
      fatal: true, mediaErrorCode: safely(() => media.error.code),
    }));
    ["offline", "online"].forEach((name) => listen(window, name, () => invoke("event", name)));
    listen(document, "visibilitychange", () => invoke("event", document.visibilityState === "hidden" ? "hidden" : "visible"));
    listen(window, "pagehide", () => invoke("event", "pagehide"));
  } catch {
    removers.forEach((remove) => safely(remove));
  }
  return {
    error: (data) => invoke("error", data),
    close: (reason) => {
      removers.forEach((remove) => safely(remove));
      removers.length = 0;
      invoke("close", reason);
    },
  };
}
